import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useApp } from "../context";
import { prepareLogo } from "../logo";
import { ErrorText } from "./ErrorText";
import { Help } from "./Help";

/**
 * The workshop's own company logo (owner, 2026-11-03), shown on the documents the workshop gives its
 * customers: the printed work order (receipt, handover, warranty) and the printed sheets. Never on the
 * program's own top bar, which stays Garagevo.
 */
export function useWorkshopLogo(): string | null {
  const { store, workshop } = useApp();
  const path = workshop.logo?.path ?? null;
  // read once per logo version and kept: the print sheet appears only for the moment of printing, so the
  // image must already be there (the work order screen calls this hook to have it ready)
  const [url, setUrl] = useState<string | null>(() => (path ? resolved.get(path) ?? null : null));
  useEffect(() => {
    let live = true;
    const logo = workshop.logo;
    if (!logo) {
      setUrl(null);
      return;
    }
    if (resolved.has(logo.path)) {
      setUrl(resolved.get(logo.path) ?? null);
      return;
    }
    void store.logoImageUrl(workshop.id, logo).then((u) => {
      resolved.set(logo.path, u);
      if (u && typeof Image === "function") new Image().src = u;
      if (live) setUrl(u);
    }, () => undefined);
    return () => {
      live = false;
    };
  }, [store, workshop.id, path]); // eslint-disable-line react-hooks/exhaustive-deps
  return url;
}

/** Logo links already read, by storage path (each upload has a new path). */
const resolved = new Map<string, string | null>();

/** The logo image, or nothing when the workshop has none (the documents then show the name only). */
export function WorkshopLogo({ className }: { className?: string }) {
  const { t } = useTranslation();
  const { workshop } = useApp();
  const url = useWorkshopLogo();
  if (!url) return null;
  return <img className={className ?? "ws-logo"} src={url} alt={t("logo.alt", { name: workshop.name })} />;
}

/** Settings, "Cég logója": upload, preview, replace and remove (owner or manager; phone and computer). */
export function LogoSettings({ canEdit }: { canEdit: boolean }) {
  const { t } = useTranslation();
  const { store, workshop, reloadWorkshop } = useApp();
  const url = useWorkshopLogo();
  const input = useRef<HTMLInputElement>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [info, setInfo] = useState<string | null>(null);
  const has = !!workshop.logo;

  const run = async (fn: () => Promise<void>, done: string) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await fn();
      await reloadWorkshop();
      setInfo(done);
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const upload = (file: File | undefined) => {
    if (!file) return;
    void run(async () => {
      const image = await prepareLogo(file);
      await store.saveLogo(workshop.id, image);
    }, t("logo.saved"));
  };

  return (
    <section className="logo-settings" aria-labelledby="logo-title">
      <h2 id="logo-title">
        {t("logo.title")} <Help id="logo.settings" />
      </h2>
      <p className="muted">{t("logo.intro")}</p>
      <div className="logo-preview" aria-label={t("logo.preview")}>
        {has && url ? (
          <img src={url} alt={t("logo.alt", { name: workshop.name })} />
        ) : (
          <span className="muted">{has ? t("common.loading") : t("logo.none")}</span>
        )}
        <strong className="logo-preview-name">{workshop.name}</strong>
      </div>
      {canEdit ? (
        <>
          <div className="actions">
            <button type="button" className="primary" disabled={busy} onClick={() => input.current?.click()}>
              {busy ? t("logo.uploading") : t(has ? "logo.replace" : "logo.upload")}
            </button>
            {has && (
              <button
                type="button"
                className="danger"
                disabled={busy}
                onClick={() => {
                  if (globalThis.confirm?.(t("logo.confirmRemove"))) void run(() => store.removeLogo(workshop.id), t("logo.removed"));
                }}
              >
                {t("logo.remove")}
              </button>
            )}
          </div>
          <input
            ref={input}
            type="file"
            accept="image/png,image/jpeg,image/webp"
            hidden
            aria-label={t("logo.file")}
            onChange={(e) => {
              upload(e.target.files?.[0]);
              e.target.value = "";
            }}
          />
          <p className="muted small">{t("logo.rules")}</p>
        </>
      ) : (
        <p className="muted small">{t("logo.readOnly")}</p>
      )}
      <ErrorText error={error} />
      {info && <p className="info" role="status">{info}</p>}
    </section>
  );
}
